import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Menu, Space, Spin, Tooltip } from "antd";
import { stageOptionsForPrint } from "utils/FieldValues";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import moment from "moment";
import _ from "lodash";
import {
  getAttendingDrList,
  getIvfId,
  getModuleWiseAllDropdownList,
  setIvfIdList,
  setIvfIdListData,
  setSelectedPatient,
} from "redux/reducers/common.slice";
import {
  clearData,
  getGlobalSearch,
} from "redux/reducers/SearchPanel/globalSearch.slice";
import { toast } from "react-toastify";
import {
  createEmbryologyData,
  editEmbryologyData,
  getEmbryologyData,
  getPatientList,
  initialPatientDetailsEDS,
  printBlankPdfEmbryoTransfer,
  printVitrificationReport,
  setBackupIvfDonerList,
  setBackupIvfList,
  setDoctorList,
  setEggsByBackupList,
  setEggsByDonorBackupList,
  setEggsByIvfDonorOptionTemp,
  setEggsByIvfOptionTemp,
  setEggsToBackupList,
  setEggsToDonorBackupList,
  setEggsToDonorPatientList,
  setEggsToIvfOptionTemp,
  setEggsToPatientList,
  setEmbryologyData,
  setEmbryologyDataUpdate,
  setEmbryologyDetails,
  setIvfIdOption,
  setPartnerDetailsEDS,
  setPatientDetailsEDS,
  setPatientListData,
  setSpermByDonorPatientList,
  setSpermByDonorSearch,
  setSpermByPatientList,
  setSpermBySearch,
} from "redux/reducers/EmbryologyData/embryologyData.slice";
import { ageCalculate, isOlderThanSixMonths } from "utils/CommonFunctions";
import {
  setIvfFlowsheetList,
  getIvfFlowsheetDetail,
} from "redux/reducers/IVFFlowSheet/IvfFlowSheet.slice";
import {
  isOpenBy,
  embryoTable,
  embryology_countTable,
  clearEmbryologyDetailsData,
  embryologyInitialData,
  procedureDetailsInitialData,
} from "./Constant";
import EmbryologyPatientDetails from "./EmbryologyPatientDetails";
import EmbryologyProcedureDetails from "./EmbryologyProcedureDetails";
import { useFormik } from "formik";
import * as Yup from "yup";

const oocyteValidationSchema = Yup.object().shape({
  no_of_oocytes: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .test(
      "combined-required-and-valid",
      "Invalid no_of_oocytes",
      function (value) {
        const { plannedCycle } = this.options.context || {};
        const { total_m2, total_m1, total_m2_used, total_m1_used } =
          this.parent;

        const toNum = (val) => {
          const num = Number(val);
          return isNaN(num) ? 0 : num;
        };

        const plannedCycleHasODorED =
          plannedCycle?.includes("OD") || plannedCycle?.includes("ED");

        // Check required condition
        if (plannedCycle && !plannedCycleHasODorED) {
          if (
            value !== 0 &&
            (value === undefined || value === null || value === "" || !value)
          ) {
            return this.createError({
              message: "Required when cycle is not OD or ED",
            });
          }
        }

        const sum = [total_m2, total_m1, total_m2_used, total_m1_used]
          .map(toNum)
          .reduce((a, b) => a + b, 0);

        const currentValue = toNum(value);

        if (!currentValue || !sum || currentValue >= sum) {
          return true;
        } else {
          return this.createError({
            message: "Should be >= M1+M2+UsedM1+UsedM2",
          });
        }
      }
    ),
  total_m2: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .test("m2-valid", "M2 exceeds limit", function (value) {
      const patient_detail = this.options.context?.patient_detail;
      const remain_m1 = patient_detail?.ivf?.remain_m1;
      const remain_m2 = patient_detail?.ivf?.remain_m2;
      const remainM2 = remain_m2 + 1;

      if (remain_m1 === 0 && remain_m2 === 0) {
        return this.createError({ message: "Unavailable" });
      }

      if (remain_m2 === null) {
        return true;
      }

      // if (value > remainM2) {
      //   return this.createError({
      //     message: "M2 exceeds limit"
      //   });
      // }

      if (value >= remainM2) {
        return this.createError({
          message: `Allow < ${remainM2}`,
        });
      }

      return true;
    }),
  total_m1: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? 0 : value))
    .test("m1-valid", "M1 exceeds limit", function (value) {
      const patient_detail = this.options.context?.patient_detail;
      const remain_m1 = patient_detail?.ivf?.remain_m1;
      const remain_m2 = patient_detail?.ivf?.remain_m2;
      const remainM1 = remain_m1 + 1;

      if (remain_m1 === null) {
        return true;
      }

      if (remain_m1 === 0 && remain_m2 === 0) {
        return this.createError({
          message: "Unavailable",
        });
      }

      // if (value > remainM1) {
      //   return this.createError({
      //     message: "M1 exceeds limit"
      //   });
      // }

      if (value >= remainM1) {
        return this.createError({
          message: `Allow < ${remainM1}`,
        });
      }

      return true;
    }),
  total_m1_used: Yup.number().nullable(),
  total_m2_used: Yup.number().nullable(),
  gv: Yup.string(),
  atretic: Yup.string(),
  fert: Yup.string(),
  cleaved: Yup.string(),
  unfert: Yup.string(),
  degenerated: Yup.string(),
  kept_for_blastocyst: Yup.string(),
  total_blastocyst: Yup.string(),
});

const oocyteTableSchema = Yup.object().shape({
  anesthesia_given_by: Yup.string().nullable(),
  anesthesia_given_by_other: Yup.string(),
  opu_done_by: Yup.string().nullable(),
  opu_done_by_other: Yup.string().nullable(),
  tesa_pesa_done_by: Yup.string().nullable(),
  tesa_pesa_done_by_other: Yup.string().nullable(),
  denudation_done_by: Yup.string().nullable(),
  denudation_done_by_other: Yup.string(),
  icsi_ivf_done_by: Yup.string().nullable(),
  icsi_ivf_done_by_other: Yup.string().nullable(),
  assisted_by: Yup.string().nullable(),
  assisted_by_other: Yup.string().nullable(),
  lmp: Yup.string(),
  opu_date: Yup.string().nullable(),
  opu_time: Yup.string().nullable(),
  actual_opu_time: Yup.string().nullable(),
  denudation_time: Yup.string().nullable(),
  icsi_ivf_time: Yup.string().nullable(),
  lh: Yup.string(),
  e2: Yup.string(),
  embryo_dev_incubator: Yup.string().nullable(),
  embryo_dev_incubator_other: Yup.string().nullable(),
  embryo_dev_culture_media: Yup.string().nullable(),
  embryo_dev_culture_media_other: Yup.string().nullable(),
  embryo_dev_batch_no_exp_date: Yup.string(),
  embryo_dev_pgt: Yup.string().nullable(),
  add_on: Yup.array().of(Yup.string()).nullable(),
  progesterone: Yup.string(),
  amh: Yup.string(),
  day_0_date: Yup.string().nullable(),
  day_0_time: Yup.string().nullable(),
  day_0_hrs_post_icsi: Yup.string(),
  day_1_date: Yup.string().nullable(),
  day_3_date: Yup.string().nullable(),
  day_5_date: Yup.string().nullable(),
  day_6_date: Yup.string().nullable(),
  day_7_date: Yup.string().nullable(),
  freezing_media: Yup.string().nullable(),
  freezing_media_other: Yup.string().nullable(),
  vitrification_batch_no_exp_date: Yup.string(),
  vitrification_devices: Yup.string().nullable(),
  vitrification_devices_other: Yup.string().nullable(),
  total_goblet: Yup.string(),
  total_straw: Yup.string(),
  tank_no: Yup.string(),
  cannister_no: Yup.string(),
  oocytes: Yup.array().of(oocyteValidationSchema),
});

export default function EmbryologyDataSheetModule() {
  // const formikRef = useRef();
  const location = useLocation();
  const dispatch = useDispatch();
  // const formDetails = formikRef.current?.formValues || {};

  let loggedUserData = localStorage.getItem("UserPreferences");
  if (loggedUserData) {
    loggedUserData = JSON.parse(window.atob(loggedUserData));
  }

  const { ivfIdListData, attendingDrList, selectedPatient } = useSelector(
    ({ common }) => common
  );
  const { moduleList, userType, selectedLocation } = useSelector(
    ({ role }) => role
  );
  const { IvfFlowsheetList, IvfFlowsheetListLoding } = useSelector(
    ({ ivfFlowSheet }) => ivfFlowSheet
  );
  const {
    embryologyData,
    patientList,
    ivfIdOption,
    embryologyDataUpdate,
    embryologyDataLoading,
    embryologyRedirect,
    embryologyDetails,
    embryology_count,
    eggsByBackupList,
    eggsByIvfOptionTemp,
    backupIvfList,
    eggsByDonorBackupList,
    eggsByIvfDonorOptionTemp,
    backupIvfDonerList,
    spermBySearch,
    spermByDonorSearch,
    patientDetailsEDS,
    partnerDetailsEDS,
    eggsToPatientList,
    eggsToBackupList,
    eggsToIvfOptionTemp,
    eggsToDonorPatientList,
  } = useSelector(({ embryologyData }) => embryologyData);

  const hasExceededSixMonths = isOlderThanSixMonths(embryologyData?.created_at);

  const [searchTrigger, setSearchTrigger] = useState("");
  const [printOption, setPrintOption] = useState([]);
  const [embryoTableList, setEmbryoTableList] = useState([]);
  const [donateByIVFData, setDonateByIVFData] = useState({});
  const [donateByIVFDataDonor, setDonateByIVFDataDonor] = useState({});
  const [eggBySelectedList, setEggBySelectedList] = useState([]);
  const [eggBySelectedListDonor, setEggBySelectedListDonor] = useState([]);

  const plannedCycleHasOD = embryologyDetails?.cycle_type?.includes("OD");

  const selectedModule = useMemo(() => {
    return (
      moduleList?.find((item) => item?.module_name === location?.pathname) || {}
    );
  }, [moduleList, location?.pathname]);

  const validateOocytes = async (values) => {
    const errors = {};

    if (!values.oocytes || !Array.isArray(values.oocytes)) {
      return errors;
    }

    for (let i = 0; i < values.oocytes.length; i++) {
      try {
        await oocyteValidationSchema.validate(values.oocytes[i], {
          context: {
            patient_detail: values.oocytes[i].patient_detail,
            // plannedCycleHasODorED: plannedCycleHasODorED
            plannedCycle: IvfFlowsheetList?.planned_cycle,
            // eggs: /* your value */,
            // cycleType: /* your value */
          },
          abortEarly: false,
        });
      } catch (err) {
        if (err.inner) {
          err.inner.forEach((error) => {
            if (!errors.oocytes) errors.oocytes = [];
            if (!errors.oocytes[i]) errors.oocytes[i] = {};
            errors.oocytes[i][error.path] = error.message;
          });
        }
      }
    }

    return errors;
  };

  const convertToCommonDateFormat = (dateString) => {
    const possibleFormats = [
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY-MM-DD",
      "DD-MM-YYYY",
      "MM-DD-YYYY",
      "YYYY/MM/DD",
      moment.ISO_8601, // For ISO formatted dates
    ];

    const parsedDate = moment(dateString, possibleFormats, true); // 'true' for strict parsing
    if (!parsedDate.isValid()) {
      return null;
    }

    return parsedDate.format("YYYY-MM-DD");
  };

  const handleSubmitForm = useCallback(
    (values) => {
      const allowedToProceed =
        loggedUserData?.other === false ||
        (loggedUserData?.other === true && !hasExceededSixMonths);

      if (allowedToProceed) {
        if (embryologyDetails?.ivf_flow_id) {
          const donorBy = [];
          const eggsBy = [];
          const eggsBydonor = [];
          const eggsIvfId = [];
          const eggsIvfDonorId = [];
          let generatedEggsToIVFId = [];
          let updatedEggsToPatients = [];

          const isProfessionalDonorPlannedCycle =
            IvfFlowsheetList?.planned_cycle?.includes("Professional Donor");

          const { oocytes, ...restProcedureDetails } = values;

          const updatedEmbryoTableData = oocytes?.map((item) => {
            if (item?.patient_detail) {
              if (item?.patient_detail.isDonor) {
                eggsBydonor.push({
                  id: item?.patient_detail.patientId,
                  name: item?.patient_detail.patientName,
                  disable: true,
                });
                eggsIvfDonorId.push({
                  _id: item?.patient_detail?.ivf._id,
                  ivf_id: item?.patient_detail?.ivf.ivf_id,
                  disable: true,
                });
              } else {
                if (
                  item?.patient_detail &&
                  selectedPatient?._id !== item?.patient_detail?.patientId
                ) {
                  eggsBy.push({
                    id: item?.patient_detail.patientId,
                    name: item?.patient_detail.patientName,
                    disable: true,
                  });
                  eggsIvfId.push({
                    _id: item?.patient_detail?.ivf._id,
                    ivf_id: item?.patient_detail?.ivf.ivf_id,
                    disable: true,
                  });
                }
              }
            }

            return {
              ...item,
              icsi_done_by_embryo_table: plannedCycleHasOD
                ? item?.icsi_done_by_embryo_table?.value || null
                : null,
              reference_ivf_id: isProfessionalDonorPlannedCycle
                ? selectedPatient?._id
                : item?.patient_detail?.isDonor
                ? patientDetailsEDS?.eggs_donor === "Donor Fresh" ||
                  patientDetailsEDS?.eggs_donor === "Donor Thawed"
                  ? item?.patient_detail?.ivf._id
                  : null
                : patientDetailsEDS?.eggs === "Donor Fresh" ||
                  patientDetailsEDS?.eggs === "Donor Thawed"
                ? item?.patient_detail?.ivf._id
                : null,

              embryo: item.embryo.map((e) => {
                const date = convertToCommonDateFormat(e?.date_of_freezing);
                const oocytes_quality = _.map(e?.oocytes_quality, "value");

                return {
                  ...e,
                  oocytes_quality,
                  embryo_grade: e?.embryo_grade?.value || null,
                  fert_check: e?.fert_check?.value || null,
                  goblet_color: e?.goblet_color?.value || null,
                  stage_of_development: e?.stage_of_development?.value || null,
                  status: e?.status?.value || null,
                  straw_color: e?.straw_color?.value || null,
                  vitrified_by: e?.vitrified_by?.value || null,
                  date_of_freezing: e?.date_of_freezing
                    ? moment(date, "YYYY-MM-DD").format("YYYY-MM-DD")
                    : null,
                };
              }),
            };
          });

          // if (patientList) {
          // patientList?.map((patient) => {
          //   if (patientDetailsEDS?.eggs_to?.includes(patient._id)) {
          //     donorBy.push({
          //       id: patient._id,
          //       name: patient.patient_full_name
          //     });
          //   }
          // }, []);
          // }

          if (isProfessionalDonorPlannedCycle) {
            const selectedEggsToIvfId = eggsToIvfOptionTemp?.filter((item) => {
              const isCurrentSelectedEggsToIVFIds =
                patientDetailsEDS.eggs_to_ivf_id.includes(item._id);
              //item?.disable for previous saved Eggs-To-IVF-Ids
              return isCurrentSelectedEggsToIVFIds || item?.disable;
            });

            updatedEggsToPatients =
              eggsToPatientList?.map((patient) => {
                return {
                  id: patient._id,
                  name: patient.patient_full_name,
                };
              }) || [];

            generatedEggsToIVFId = selectedEggsToIvfId.map((item) => ({
              _id: item._id,
              ivf_id: item.ivf_id,
              disable: true,
            }));
          }

          const updatedEggsToDonorPatients =
            eggsToDonorPatientList?.map((patient) => {
              return {
                id: patient._id,
                name: patient.patient_full_name,
              };
            }) || [];

          const payloadObj = {
            ...patientDetailsEDS,
            ...partnerDetailsEDS,
            ...restProcedureDetails,
            embryology_count: updatedEmbryoTableData,
            eggs_by: eggsBy,
            eggs_by_donor: eggsBydonor,
            eggs_by_ivf_id: eggsIvfId,
            eggs_by_ivf_id_donor: eggsIvfDonorId,
            // eggs_to: donorBy,
            eggs_to: updatedEggsToPatients,
            eggs_to_ivf_id: generatedEggsToIVFId,
            eggs_to_donor: updatedEggsToDonorPatients,
            icsi_ivf_done_by:
              plannedCycleHasOD || values?.icsi_ivf_done_by === "Other"
                ? null
                : values?.icsi_ivf_done_by,
            opu_done_by:
              values?.opu_done_by === "Other" ? null : values?.opu_done_by,
            tesa_pesa_done_by:
              values?.tesa_pesa_done_by === "Other"
                ? null
                : values?.tesa_pesa_done_by,
            assisted_by:
              values?.assisted_by === "Other" ? null : values?.assisted_by,
            actual_opu_time: values?.actual_opu_time
              ? (values?.actual_opu_time).format("HH:mm:ss")
              : null,
            day_0_time: values?.day_0_time
              ? (values?.day_0_time).format("HH:mm:ss")
              : null,
            denudation_time: values?.denudation_time
              ? (values?.denudation_time).format("HH:mm:ss")
              : null,
            icsi_ivf_time: values?.icsi_ivf_time
              ? (values?.icsi_ivf_time).format("HH:mm:ss")
              : null,
            lmp: values?.lmp ? (values?.lmp).format("YYYY-MM-DD") : null,
            opu_date: values?.opu_date
              ? (values?.opu_date).format("YYYY-MM-DD")
              : null,
            opu_time: values?.opu_time
              ? (values?.opu_time).format("HH:mm:ss")
              : null,
            // sperms_by_donor: spermsByDetailDonor?._id,
            // sperms_by_name_donor: spermsByDetailDonor?.patient_full_name,
          };

          if (Object.keys(embryologyData)?.length > 0) {
            dispatch(
              editEmbryologyData({
                location_id: selectedLocation,
                _id: embryologyDetails?._id,
                module_id: selectedModule?._id,
                payload: payloadObj,
              })
            );
          } else {
            dispatch(
              createEmbryologyData({
                location_id: selectedLocation,
                patient_reg_id: selectedPatient?._id,
                module_id: selectedModule?._id,
                payload: payloadObj,
              })
            );
          }
        } else {
          toast.error("IVF id Is Not Generated");
        }
      }
    },
    [
      dispatch,
      hasExceededSixMonths,
      embryologyData,
      selectedLocation,
      partnerDetailsEDS,
      patientDetailsEDS,
      selectedModule?._id,
      selectedPatient?._id,
      embryologyDetails?._id,
      embryologyDetails?.ivf_flow_id,
      eggsToDonorPatientList,
      eggsToPatientList,
      plannedCycleHasOD,
      eggsToIvfOptionTemp,
      IvfFlowsheetList?.planned_cycle,
    ]
  );

  const plannedCycleHasODorED = useMemo(() => {
    return (
      IvfFlowsheetList?.planned_cycle?.includes("OD") ||
      IvfFlowsheetList?.planned_cycle?.includes("ED")
    );
  }, [IvfFlowsheetList]);

  const formik = useFormik({
    initialValues: procedureDetailsInitialData,
    validate: validateOocytes,
    validationSchema: oocyteTableSchema,
    validationContext: {
      plannedCycleHasODorED: plannedCycleHasODorED,
    },
    onSubmit: handleSubmitForm,
  });

  const {
    values,
    errors,
    touched,
    setValues,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldError,
  } = formik;

  const ivfFlowSheetDataModule = useMemo(() => {
    return (
      moduleList?.find((item) => item?.module_name === "/ivf-flow-sheet") || {}
    );
  }, [moduleList]);

  const fetchDropdownData = useCallback(async () => {
    dispatch(
      getModuleWiseAllDropdownList({
        location_id: selectedLocation,
        module_id: selectedModule?._id,
      })
    );
  }, [dispatch, selectedLocation, selectedModule?._id]);

  useEffect(() => {
    if (selectedModule?._id && selectedLocation) {
      fetchDropdownData();
    }
  }, [fetchDropdownData, selectedModule, selectedLocation]);

  useEffect(() => {
    if (Object.keys(selectedPatient).length > 0) {
      dispatch(getAttendingDrList());
    }
  }, [dispatch, selectedPatient]);

  useEffect(() => {
    if (
      Object.keys(selectedModule)?.length > 0 &&
      Object.keys(selectedPatient)?.length > 0 &&
      selectedLocation
    ) {
      dispatch(
        getIvfId({
          locationId: selectedLocation,
          patientRegId: selectedPatient?._id,
          moduleId: selectedModule?._id,
        })
      );
    }
    return () => {
      clearEmbryologyData();
      dispatch(setIvfIdList([]));
      dispatch(setEmbryologyData({}));
      dispatch(setIvfIdOption([]));
      dispatch(setEggsByBackupList([]));
    };
  }, [selectedLocation, selectedModule, selectedPatient]);

  useMemo(() => {
    if (attendingDrList?.length > 0) {
      dispatch(
        setDoctorList(
          attendingDrList.map((item, index) => ({
            value: item._id,
            label: item.user_name,
          }))
        )
      );
    }
  }, [attendingDrList]);

  useEffect(() => {
    if (patientList?.length > 0) {
      // if (searchTrigger === "sperms_by") {
      //   const mergedList = [...patientList, ...spermBySearch];
      //   dispatch(setSpermBySearch(mergedList));
      // }
      if (searchTrigger === "sperms_by_donor") {
        const mergedList = [...patientList, ...spermByDonorSearch];
        dispatch(setSpermByDonorSearch(mergedList));
      }
    }
  }, [patientList]);

  const createPrintOption = useCallback((stageSet) => {
    const filteredStageSet = stageSet?.filter((item) => {
      return stageOptionsForPrint?.some((option) => option.value === item);
    });

    const uniqueOptions = filteredStageSet?.map((stage) => ({
      label: stage,
      value: stage,
    }));

    return uniqueOptions;
  }, []);

  const sortedEmbryologyCountData = (
    embryoCountData,
    plannedCycle,
    selectedIvfFlowId
  ) => {
    const stageSet = new Set();

    // embryoCountData?.forEach((record) => {
    //   record.embryo.forEach((embryo) => {
    //     if (embryo.stage_of_development) {
    //       stageSet.add(embryo.stage_of_development);
    //     }
    //   });
    // });
    // const uniqueStages = Array.from(stageSet);
    // const printOp = createPrintOption(uniqueStages);
    // setPrintOption(printOp);

    let sortedEmbryoTableData = [];

    if (embryoCountData?.length > 0) {
      sortedEmbryoTableData = embryoCountData.map((item) => {
        const sortedEmbryos = [...item.embryo].sort(
          (a, b) => +a.oocytes - +b.oocytes
        );

        /**/ // If selected self patient IVF-Id Oocyte table and, not receive ivf_id and _id in patient-details then it will work.
        let patientDetails = { ...item.patient_detail };

        const isExistIVFId = item.patient_detail.ivf?.ivf_id;
        const isExistId = item.patient_detail.ivf?._id;
        // const allIvfOptions = eggsByIvfOptionTemp ?? [];

        const isSelfPatientOocyteTable =
          item?.patient_detail?.patientId === selectedPatient?._id;

        // const isCheckEggsByIVFIdIsNull = embryologyResponseData.eggs_by_ivf_id.find((item) => item._id)

        if (!isExistId && !isExistIVFId) {
          if (isSelfPatientOocyteTable) {
            const selectedIvf = ivfIdOption.find(
              (item) => item.value === selectedIvfFlowId
            );

            patientDetails = {
              ...patientDetails,
              ivf: {
                ...item.patient_detail.ivf,
                ivf_id: selectedIvf?.label,
                _id: selectedIvf?.value,
              },
            };
          }
        }
        /**/

        const updatedEmbryos = sortedEmbryos.map((emb, index) => {
          const stageOfDevelopment = plannedCycle?.includes(
            "Oocytes Vitrification"
          )
            ? { label: emb.maturation_stage, value: emb.maturation_stage }
            : emb.stage_of_development
            ? {
                label: emb.stage_of_development,
                value: emb.stage_of_development,
              }
            : null;

          if (emb.stage_of_development) {
            stageSet.add(emb.stage_of_development);
          }

          const oocytesQuality = (emb.oocytes_quality || []).map((quality) => ({
            label: quality,
            value: quality,
          }));

          const fert_check = emb.fert_check
            ? { label: emb.fert_check, value: emb.fert_check }
            : null;
          const embryo_grade = emb.embryo_grade
            ? { label: emb.embryo_grade, value: emb.embryo_grade }
            : null;
          const straw_color = emb.straw_color
            ? { label: emb.straw_color, value: emb.straw_color }
            : null;
          const date_of_freezing = emb.date_of_freezing
            ? moment(emb.date_of_freezing).format("YYYY-MM-DD")
            : "";
          const goblet_color = emb.goblet_color
            ? { label: emb.goblet_color, value: emb.goblet_color }
            : null;
          const vitrified_by = emb.vitrified_by
            ? { label: emb.vitrified_by, value: emb.vitrified_by }
            : null;
          const status = emb.status
            ? { label: emb.status, value: emb.status }
            : null;

          const hasVitrificationId = !(
            _.isNil(emb?.vitrification_id) || _.isEmpty(emb?.vitrification_id)
          );

          return {
            ...emb,
            hasVitrificationId, // Its for disable the VitrificationId field.
            oocytes: index + 1,
            stage_of_development: stageOfDevelopment,
            oocytes_quality: oocytesQuality,
            fert_check,
            embryo_grade,
            straw_color,
            date_of_freezing,
            goblet_color,
            vitrified_by,
            status,
          };
        });

        return {
          ...item,
          patient_detail: patientDetails,
          icsi_done_by_embryo_table: item.icsi_done_by_embryo_table
            ? {
                label: item.icsi_done_by_embryo_table,
                value: item.icsi_done_by_embryo_table,
              }
            : null,
          embryo: updatedEmbryos,
        };
      });
    } else {
      const selectedIvf = ivfIdOption.find(
        (item) => item.value === selectedIvfFlowId
      );

      const embryoData = [
        {
          ...embryology_countTable,
          patient_detail: {
            patientId: selectedPatient?._id,
            patientName: selectedPatient?.patient_full_name,
            ivf: { ivf_id: selectedIvf?.label, _id: selectedIvf?.value },
            isDonor: false,
          },
          embryo: [],
        },
      ];

      return embryoData;
    }

    const uniqueStages = Array.from(stageSet);
    const printOp = createPrintOption(uniqueStages);
    setPrintOption(printOp);

    return sortedEmbryoTableData;
  };

  useEffect(() => {
    if (
      embryologyDetails?.ivf_flow_id &&
      selectedPatient?._id &&
      embryologyDetails?.ivf_flow_id !== "new"
    ) {
      let embryologyResponse = {};

      dispatch(
        getEmbryologyData({
          location_id: selectedLocation,
          patient_reg_id: selectedPatient?._id,
          module_id: selectedModule?._id,
          ivf_flow_id: embryologyDetails?.ivf_flow_id,
        })
      ).then(async (res) => {
        embryologyResponse = res?.payload;
        let selectedEmbryologyDetails = { ...embryologyDetails };

        // if (embryologyResponse && Object.keys(embryologyResponse)?.length) {
        const { embryologyDataObject, procedureDetailsObj: procedureDetails } =
          await handleSetEmbryologyDetails(embryologyResponse, false);

        selectedEmbryologyDetails = embryologyDataObject;
        // }
        if (
          embryologyResponse?.eggs_by &&
          embryologyResponse?.eggs_by_name &&
          selectedLocation
        ) {
          dispatch(
            getPatientList({
              patient_name: embryologyResponse?.eggs_by_name,
              start: null,
              limit: null,
              patient_reg_id: selectedPatient?._id,
            })
          )
            .then((result) => {})
            .catch((err) => {
              console.error("Error Fetching IVF List", err);
            });
        }

        dispatch(
          getIvfFlowsheetDetail({
            locationId: selectedLocation,
            ivfFlowId: embryologyDetails?.ivf_flow_id,
            moduleId: ivfFlowSheetDataModule?._id,
          })
        ).then((res) => {
          let partnerAge = "";
          const lastMenstrualPeriod = res?.payload?.last_menstrual_period;
          const pickupDate = res?.payload?.pick_up_date;
          const pickupTime = res?.payload?.pick_up_time;
          const plannedCycle = res?.payload?.planned_cycle || [];
          const embryoCountData = embryologyResponse?.embryology_count ?? [];
          const cycleTypeIncludesOD = plannedCycle.includes("OD");

          if (cycleTypeIncludesOD && embryologyResponse?.donor_partner_age) {
            // Parse the dates using Moment.js
            let startDate = moment(embryologyResponse?.donor_partner_age);
            let endDate = moment();

            // Calculate the difference in years
            let differenceInYears = endDate.diff(startDate, "years");
            partnerAge = differenceInYears;
          }

          if (!embryologyResponse) {
            const patientDetailsObj = {
              ...patientDetailsEDS,
              ivf_flow_id: res?.payload?._id,
            };

            dispatch(setPatientDetailsEDS(patientDetailsObj));
          }

          const procedureDetailsObj = {
            // ...formikRef.current?.formValues,
            // ...values,
            ...procedureDetails,
            lmp: lastMenstrualPeriod
              ? dayjs(
                  moment(lastMenstrualPeriod).format("DD/MM/YYYY"),
                  "DD/MM/YYYY"
                )
              : "",
            opu_date: pickupDate
              ? dayjs(moment(pickupDate).format("YYYY-MM-DD"), "YYYY-MM-DD")
              : "",
            //   opu_date: embryologyData?.opu_date
            // ? moment(embryologyData?.opu_date).format("DD/MM/YYYY")
            // : null,
            opu_time: pickupTime ? dayjs(pickupTime, "HH:mm:ss") : "",
            day_0_date: cycleTypeIncludesOD
              ? embryologyResponse?.day_0_date
                ? moment(new Date(embryologyResponse?.day_0_date)).format(
                    "YYYY-MM-DD"
                  )
                : null
              : pickupDate
              ? moment(new Date(pickupDate)).format("YYYY-MM-DD")
              : null,
            day_0_time: embryologyResponse
              ? embryologyResponse?.day_0_time
                ? dayjs(embryologyResponse?.day_0_time, "HH:mm:ss")
                : ""
              : pickupTime
              ? dayjs(pickupTime, "HH:mm:ss")
              : "",
            day_1_date: cycleTypeIncludesOD
              ? embryologyResponse?.day_0_date
                ? moment(new Date(embryologyResponse?.day_0_date))
                    .add(1, "days")
                    .format("YYYY-MM-DD")
                : null
              : pickupDate
              ? moment(new Date(pickupDate)).add(1, "days").format("YYYY-MM-DD")
              : null,

            // opu_date: embryologyData?.opu_date
            // ? moment(embryologyData?.opu_date).format("DD/MM/YYYY")
            // : null,

            day_3_date: cycleTypeIncludesOD
              ? embryologyResponse?.day_0_date
                ? moment(new Date(embryologyResponse?.day_0_date))
                    .add(3, "days")
                    .format("YYYY-MM-DD")
                : null
              : pickupDate
              ? moment(new Date(pickupDate)).add(3, "days").format("YYYY-MM-DD")
              : null,
            day_5_date: cycleTypeIncludesOD
              ? embryologyResponse?.day_0_date
                ? moment(new Date(embryologyResponse?.day_0_date))
                    .add(5, "days")
                    .format("YYYY-MM-DD")
                : null
              : pickupDate
              ? moment(new Date(pickupDate)).add(5, "days").format("YYYY-MM-DD")
              : null,
            day_6_date: cycleTypeIncludesOD
              ? embryologyResponse?.day_0_date
                ? moment(new Date(embryologyResponse?.day_0_date))
                    .add(6, "days")
                    .format("YYYY-MM-DD")
                : null
              : pickupDate
              ? moment(new Date(pickupDate)).add(6, "days").format("YYYY-MM-DD")
              : null,
            day_7_date: cycleTypeIncludesOD
              ? embryologyResponse?.day_0_date
                ? moment(new Date(embryologyResponse?.day_0_date))
                    .add(7, "days")
                    .format("YYYY-MM-DD")
                : null
              : pickupDate
              ? moment(new Date(pickupDate)).add(7, "days").format("YYYY-MM-DD")
              : null,
            lh: embryologyResponse?.lh
              ? embryologyResponse?.lh
              : res?.payload.eds_lh || "",
            e2: embryologyResponse?.e2
              ? embryologyResponse?.e2
              : res?.payload.eds_e2 || "",
            amh: embryologyResponse?.amh
              ? embryologyResponse?.amh
              : res?.payload.eds_amh || "",
            progesterone: embryologyResponse?.progesterone
              ? embryologyResponse?.progesterone
              : res?.payload.eds_progesterone || "",
          };

          const updatedEmbryologyTableData = {
            ...selectedEmbryologyDetails,
            cycle_type: plannedCycle,
            donor_partner_name: plannedCycle.includes("OD")
              ? embryologyResponse?.donor_partner_name
              : "",
            donor_partner_age: partnerAge ? partnerAge : null,
          };

          setValues(procedureDetailsObj);
          // formikRef.current?.setValues(procedureDetailsObj);
          dispatch(setEmbryologyDetails(updatedEmbryologyTableData));

          const sortedEmbryoTableData = sortedEmbryologyCountData(
            embryoCountData,
            plannedCycle,
            selectedEmbryologyDetails?.ivf_flow_id
          );

          // dispatch(setEmbryology_count(sortEmbro));
          // formikRef.current?.setFieldValue("oocytes", sortedEmbryoTableData);
          setFieldValue("oocytes", sortedEmbryoTableData);
        });
      });
    } else if (
      embryologyDetails?.ivf_flow_id &&
      selectedPatient?._id &&
      embryologyDetails?.ivf_flow_id === "new"
    ) {
      let startDate = moment(selectedPatient?.partner_dob);
      let endDate = moment();

      // Calculate the difference in years
      let differenceInYears = endDate.diff(startDate, "years");

      dispatch(
        setEmbryologyDetails({
          ...embryologyDetails,
          donor_partner_name: selectedPatient?.partner_full_name,
          donor_partner_age: differenceInYears ? differenceInYears : null,
        })
      );
    }
  }, [embryologyDetails?.ivf_flow_id]);

  useEffect(() => {
    if (embryologyDataUpdate) {
      dispatch(
        getEmbryologyData({
          location_id: selectedLocation,
          patient_reg_id: selectedPatient?._id,
          module_id: selectedModule?._id,
          ivf_flow_id: embryologyDetails?.ivf_flow_id,
        })
      )
        .then((result) => {
          const plannedCycle = embryologyDetails?.cycle_type;
          const embryoCountData = result?.payload?.embryology_count ?? [];

          // let sortEmbro = JSON.parse(
          //   JSON.stringify(result?.payload?.embryology_count || [])
          // );

          // const stageSet = new Set();
          // result?.payload?.embryology_count?.forEach((record) => {
          //   record.embryo.forEach((embryo) => {
          //     if (embryo.stage_of_development) {
          //       stageSet.add(embryo.stage_of_development);
          //     }
          //   });
          // });

          // sortEmbro.forEach((item) => {
          //   item.embryo.sort((a, b) => +a.oocytes - +b.oocytes);
          //   item.embryo.forEach((emb) => {
          //     emb.stage_of_development =
          //       IvfFlowsheetList?.planned_cycle?.includes(
          //         "Oocytes Vitrification"
          //       )
          //         ? emb.maturation_stage
          //         : emb.stage_of_development;
          //   });
          // });

          const sortedEmbryoTableData = sortedEmbryologyCountData(
            embryoCountData,
            plannedCycle
          );

          //   dispatch(setEmbryology_count(sortEmbro));
          // const uniqueStages = Array.from(stageSet);
          // const printOp = createPrintOption(uniqueStages);
          // setPrintOption(printOp);

          setFieldValue("oocytes", sortedEmbryoTableData);
          // formikRef.current?.setFieldValue("oocytes", sortedEmbryoTableData);
        })
        .catch((err) => {
          console.error("Error While Fetching Updated Data", err);
        });
      dispatch(setEmbryologyDataUpdate(false));
    }
  }, [embryologyDataUpdate]);

  const handleSetEmbryologyDetails = useCallback(
    (embryologyData, isSetData = true) => {
      // if (Object.keys(embryologyData).length > 0) {
      let updatedEggsToOptions = [];
      let updatedEggsToDonorOptions = [];

      const updatedEmbryologyData = embryologyData?.embryo?.map((item) => {
        return {
          ...item,
          vitrified_by: item?.vitrified_by
            ? item?.vitrified_by?.split(",")
            : [],
        };
      });

      // const stageSet = new Set();
      // embryologyData?.embryology_count?.forEach((record) => {
      //   record.embryo.forEach((embryo) => {
      //     if (embryo.stage_of_development) {
      //       stageSet.add(embryo.stage_of_development);
      //     }
      //   });
      // });
      // const uniqueStages = Array.from(stageSet);
      // const printOp = createPrintOption(uniqueStages);
      // setPrintOption(printOp);

      // const newPackageData = transformData(updatedEmbryologyData || []);

      setEmbryoTableList(updatedEmbryologyData || [embryoTable]);
      // if (isOpenBy.includes(embryologyData?.eggs)) {
      //   // dispatch(
      //   //   setPatientListData([
      //   //     {
      //   //       _id: embryologyData?.eggs_by,
      //   //       patient_full_name: embryologyData?.eggs_by_name,
      //   //     },
      //   //   ])
      //   // );
      // }

      if (isOpenBy.includes(embryologyData?.sperms)) {
        dispatch(
          setSpermBySearch([
            {
              _id: embryologyData?.sperms_by,
              patient_full_name: embryologyData?.sperms_by_name,
            },
          ])
        );
        //   form.setFieldsValue({
        //     sperms_by: embryologyData?.sperms_by
        //   });
      }
      if (embryologyData?.eggs_by_ivf_id.length) {
        const eggIvfData = {
          ivf_flow_data: embryologyData?.eggs_by_ivf_id,
        };
        setDonateByIVFData(eggIvfData);
      }
      if (embryologyData?.eggs_by_ivf_id_donor?.length) {
        const egg = {
          ivf_flow_data: embryologyData?.eggs_by_ivf_id_donor,
        };
        setDonateByIVFDataDonor(egg);
      }
      if (isOpenBy.includes(embryologyData?.sperms_donor)) {
        dispatch(
          setSpermByDonorSearch([
            {
              _id: embryologyData?.sperms_by_donor,
              patient_full_name: embryologyData?.sperms_by_name_donor,
            },
          ])
        );
        //   form.setFieldsValue({
        //     sperms_by_donor: embryologyData?.sperms_by_name_donor
        //   });
        // dispatch(
        //   setPatientListData([
        //     {
        //       _id: embryologyData?.sperms_by_donor,
        //       patient_full_name: embryologyData?.sperms_by_name_donor,
        //     },
        //   ])
        // );
      }

      if (embryologyData?.sperms_by_donor) {
        dispatch(
          setSpermByDonorPatientList([
            {
              _id: embryologyData?.sperms_by_donor,
              patient_full_name: embryologyData?.sperms_by_name_donor,
            },
          ])
        );
      }

      if (embryologyData?.sperms_by) {
        dispatch(
          setSpermByPatientList([
            {
              _id: embryologyData?.sperms_by,
              patient_full_name: embryologyData?.sperms_by_name,
            },
          ])
        );
      }

      if (embryologyData?.eggs_to?.length > 0) {
        updatedEggsToOptions = embryologyData?.eggs_to?.map((item) => {
          return {
            _id: item?.id,
            patient_full_name: item?.name,
          };
        });

        dispatch(setEggsToPatientList(updatedEggsToOptions));
        dispatch(setEggsToBackupList(updatedEggsToOptions));
      }

      if (embryologyData?.eggs_to_donor?.length > 0) {
        updatedEggsToDonorOptions = embryologyData?.eggs_to_donor?.map(
          (item) => {
            return {
              _id: item?.id,
              patient_full_name: item?.name,
            };
          }
        );

        dispatch(setEggsToDonorPatientList(updatedEggsToDonorOptions));
        dispatch(setEggsToDonorBackupList(updatedEggsToDonorOptions));
      }

      const isDonorEggsValue = isOpenBy.includes(embryologyData?.eggs);

      // const embryologyEggsBySelectedData =
      //   embryologyData?.eggs_by
      //     ?.filter((item) => item?.id && item.id !== selectedPatient?._id)
      //     .map((item) => item.id) || [];

      const embryologyEggsBySelectedData = _.uniq(
        _.map(
          _.filter(
            embryologyData?.eggs_by,
            (item) => item?.id && item.id !== selectedPatient?._id
          ),
          "id"
        )
      );

      const embryologyEggsByIVFIdSelectedData =
        embryologyData?.eggs_by_ivf_id
          ?.filter((item) => item?._id && item._id !== selectedPatient?._id)
          .map((item) => item._id) || [];

      const patientDetailsObj = {
        eggs: embryologyData?.eggs || null,
        eggs_by:
          isDonorEggsValue && embryologyData?.eggs_by?.length > 0
            ? embryologyEggsBySelectedData
            : [],
        eggs_by_ivf_id:
          embryologyData?.eggs_by_ivf_id?.length > 0
            ? embryologyEggsByIVFIdSelectedData
            : [],
        eggs_to:
          embryologyData?.eggs_to?.length > 0
            ? embryologyData?.eggs_to?.map((_) => _.id)
            : [],
        ivf_flow_id: embryologyData?.ivf_flow_id ?? "",
        eggs_donor: embryologyData?.eggs_donor || null,
        eggs_by_donor:
          embryologyData?.eggs_by_donor?.length > 0
            ? embryologyData?.eggs_by_donor?.map((_) => _.id)
            : [],
        eggs_by_ivf_id_donor:
          embryologyData?.eggs_by_ivf_id_donor?.length > 0
            ? embryologyData?.eggs_by_ivf_id_donor?.map((_) => _._id)
            : [],
        eggs_to_donor: embryologyData?.eggs_to_donor?.length
          ? embryologyData?.eggs_to_donor?.map((_) => _.id)
          : [],
      };

      const partnerDetailsObj = {
        sperms: embryologyData?.sperms || null,
        sperms_by: embryologyData?.sperms_by,
        sperms_quality: embryologyData?.sperms_quality || null,
        sperms_quality_other: embryologyData?.sperms_quality_other || "",
        sperms_prep_method: embryologyData?.sperms_prep_method || null,
        sperms_prep_method_other:
          embryologyData?.sperms_prep_method_other || "",
        frozen_vial_id: embryologyData?.frozen_vial_id || "",
        sperms_donor: embryologyData?.sperms_donor || null,
        sperms_by_donor: embryologyData?.sperms_by_donor || null,
        sperms_quality_donor: embryologyData?.sperms_quality_donor || null,
        sperms_quality_other_donor:
          embryologyData?.sperms_quality_other_donor || "",
        sperms_prep_method_donor:
          embryologyData?.sperms_prep_method_donor || null,
        sperms_prep_method_other_donor:
          embryologyData?.sperms_prep_method_other_donor || "",
        frozen_vial_id_donor: embryologyData?.frozen_vial_id_donor || "",
      };

      const procedureDetailsObj = {
        // ...formDetails,
        ...values,
        anesthesia_given_by: embryologyData?.anesthesia_given_by || null,
        anesthesia_given_by_other:
          embryologyData?.anesthesia_given_by_other || "",
        opu_done_by: embryologyData?.opu_done_by
          ? embryologyData.opu_done_by
          : embryologyData?.opu_done_by_other
          ? "Other"
          : null,
        opu_done_by_other: embryologyData?.opu_done_by_other || "",
        tesa_pesa_done_by: embryologyData?.tesa_pesa_done_by
          ? embryologyData.tesa_pesa_done_by
          : embryologyData?.tesa_pesa_done_by_other
          ? "Other"
          : null,
        tesa_pesa_done_by_other: embryologyData?.tesa_pesa_done_by_other || "",
        denudation_done_by: embryologyData?.denudation_done_by || null,
        denudation_done_by_other:
          embryologyData?.denudation_done_by_other || "",
        icsi_ivf_done_by: embryologyData?.icsi_ivf_done_by
          ? embryologyData.icsi_ivf_done_by
          : embryologyData?.icsi_ivf_done_by_other
          ? "Other"
          : null,
        icsi_ivf_done_by_other: embryologyData?.icsi_ivf_done_by_other || "",
        assisted_by: embryologyData?.assisted_by
          ? embryologyData.assisted_by
          : embryologyData?.assisted_by_other
          ? "Other"
          : null,
        assisted_by_other: embryologyData?.assisted_by_other || "",
        //   lmp,
        opu_date: embryologyData?.opu_date
          ? moment(embryologyData?.opu_date).format("DD/MM/YYYY")
          : null,
        opu_time: embryologyData?.opu_time
          ? dayjs(embryologyData?.opu_time, "HH:mm:ss")
          : null,
        actual_opu_time: embryologyData?.actual_opu_time
          ? dayjs(embryologyData?.actual_opu_time, "HH:mm:ss")
          : null,
        denudation_time: embryologyData?.denudation_time
          ? dayjs(embryologyData?.denudation_time, "HH:mm:ss")
          : null,
        icsi_ivf_time: embryologyData?.icsi_ivf_time
          ? dayjs(embryologyData?.icsi_ivf_time, "HH:mm:ss")
          : null,
        lh: embryologyData?.lh || "--",
        e2: embryologyData?.e2 || "--",
        embryo_dev_incubator: embryologyData?.embryo_dev_incubator || null,
        embryo_dev_incubator_other:
          embryologyData?.embryo_dev_incubator_other || "",
        embryo_dev_culture_media:
          embryologyData?.embryo_dev_culture_media || null,
        embryo_dev_culture_media_other:
          embryologyData?.embryo_dev_culture_media_other || null,
        embryo_dev_batch_no_exp_date:
          embryologyData?.embryo_dev_batch_no_exp_date || "",
        embryo_dev_pgt: embryologyData?.embryo_dev_pgt || null,
        add_on: embryologyData?.add_on || null,
        progesterone: embryologyData?.progesterone || "--",
        amh: embryologyData?.amh || "--",
        //   day_0_date,
        day_0_time: embryologyData?.day_0_time
          ? dayjs(embryologyData?.day_0_time, "HH:mm:ss")
          : null,
        day_0_hrs_post_icsi: embryologyData?.day_0_hrs_post_icsi || "--",
        //   day_1_date,
        //   day_3_date,
        day_5_date: embryologyData?.day_5_date
          ? moment(embryologyData?.day_5_date).format("DD/MM/YYYY")
          : null,
        day_6_date: embryologyData?.day_6_date
          ? moment(embryologyData?.day_6_date).format("DD/MM/YYYY")
          : null,
        //   day_7_date,
        freezing_media: embryologyData?.freezing_media || null,
        freezing_media_other: embryologyData?.freezing_media_other || null,
        vitrification_batch_no_exp_date:
          embryologyData?.vitrification_batch_no_exp_date || "--",
        vitrification_devices: embryologyData?.vitrification_devices || null,
        vitrification_devices_other:
          embryologyData?.vitrification_devices_other || null,
        total_goblet: embryologyData?.total_goblet || 0,
        total_straw: embryologyData?.total_straw || 0,
        tank_no: embryologyData?.tank_no || "",
        cannister_no: embryologyData?.cannister_no || "",
      };

      setValues(procedureDetailsObj);
      // formikRef.current?.setValues(procedureDetailsObj);
      dispatch(
        setPatientDetailsEDS({ ...patientDetailsEDS, ...patientDetailsObj })
      );
      dispatch(setPartnerDetailsEDS(partnerDetailsObj));

      const embryologyDataObject = {
        ...embryologyDetails,
        // ivf_flow_id: embryologyData?.ivf_flow_id,
        _id: embryologyData?._id,
        //   eggs: embryologyData?.eggs || null,
        cycle_type: embryologyDetails?.cycle_type || null,
        cycle_no: embryologyData?.cycle_no || "--",
        // opu_done_by: embryologyData?.opu_done_by || null,
        //   opu_done_by: embryologyData?.opu_done_by
        //     ? embryologyData.opu_done_by
        //     : embryologyData?.opu_done_by_other
        //     ? "Other"
        //     : null,
        //   opu_done_by_other: embryologyData?.opu_done_by_other || "",
        //   anesthesia_given_by: embryologyData?.anesthesia_given_by || null,
        //   denudation_done_by: embryologyData?.denudation_done_by || null,
        //   anesthesia_given_by_other:
        //     embryologyData?.anesthesia_given_by_other || "",
        //   denudation_done_by_other:
        //     embryologyData?.denudation_done_by_other || "",
        //   tesa_pesa_done_by: embryologyData?.tesa_pesa_done_by
        //     ? embryologyData.tesa_pesa_done_by
        //     : embryologyData?.tesa_pesa_done_by_other
        //     ? "Other"
        //     : null,
        // donor_partner_name: embryologyData?.donor_partner_name || "--",
        // donor_partner_age: embryologyData?.donor_partner_age || "--",
        //   sperms: embryologyData?.sperms || null,
        //   sperms_quality: embryologyData?.sperms_quality || null,
        //   sperms_quality_donor: embryologyData?.sperms_quality_donor || null,
        //   sperms_quality_other_donor:
        //     embryologyData?.sperms_quality_other_donor || "",
        //   sperms_prep_method_donor:
        //     embryologyData?.sperms_prep_method_donor || null,
        //   sperms_prep_method_other_donor:
        //     embryologyData?.sperms_prep_method_other_donor || "",
        //   sperms_donor: embryologyData?.sperms_donor || null,
        //   frozen_vial_id_donor: embryologyData?.frozen_vial_id_donor || "",
        //   sperms_prep_method: embryologyData?.sperms_prep_method || null,
        //   add_on: embryologyData?.add_on || null,
        //   frozen_vial_id: embryologyData?.frozen_vial_id || "",
        //   icsi_ivf_done_by: embryologyData?.icsi_ivf_done_by
        //     ? embryologyData.icsi_ivf_done_by
        //     : embryologyData?.icsi_ivf_done_by_other
        //     ? "Other"
        //     : null,
        //   assisted_by: embryologyData?.assisted_by
        //     ? embryologyData.assisted_by
        //     : embryologyData?.assisted_by_other
        //     ? "Other"
        //     : null,
        //   opu_date: embryologyData?.opu_date
        //     ? moment(embryologyData?.opu_date).format("DD/MM/YYYY")
        //     : null,
        //   opu_time: embryologyData?.opu_time
        //     ? dayjs(embryologyData?.opu_time, "HH:mm:ss").format("HH:mm:ss")
        //     : null,
        //   actual_opu_time: embryologyData?.actual_opu_time
        //     ? dayjs(embryologyData?.actual_opu_time, "HH:mm:ss").format(
        //         "HH:mm:ss"
        //       )
        //     : null,
        //   amh: embryologyData?.amh || "--",
        //   progesterone: embryologyData?.progesterone || "--",
        // lmp: (embryologyData?.lmp !== '--' && embryologyData?.lmp)
        //   ? dayjs(
        //     moment(embryologyData?.lmp).format(
        //       "DD/MM/YYYY"
        //     ),
        //     "DD/MM/YYYY"
        //   )
        //   : "",
        //   lh: embryologyData?.lh || "--",
        //   e2: embryologyData?.e2 || "--",
        atretic: embryologyData?.atretic || "",
        //   embryo_dev_pgt: embryologyData?.embryo_dev_pgt || null,
        //   denudation_time: embryologyData?.denudation_time
        //     ? dayjs(embryologyData?.denudation_time, "HH:mm:ss").format(
        //         "HH:mm:ss"
        //       )
        //     : null,
        //   icsi_ivf_time: embryologyData?.icsi_ivf_time
        //     ? dayjs(embryologyData?.icsi_ivf_time, "HH:mm:ss").format(
        //         "HH:mm:ss"
        //       )
        //     : null,
        //   embryo_dev_culture_media:
        //     embryologyData?.embryo_dev_culture_media || null,
        //   embryo_dev_culture_media_other:
        //     embryologyData?.embryo_dev_culture_media_other || null,
        //   freezing_media_other: embryologyData?.freezing_media_other || null,
        //   vitrification_devices_other:
        //     embryologyData?.vitrification_devices_other || null,
        //   icsi_ivf_done_by_other: embryologyData?.icsi_ivf_done_by_other || "",
        //   sperms_by: embryologyData?.sperms_by,
        //   sperms_prep_method_other:
        //     embryologyData?.sperms_prep_method_other || "",
        //   sperms_quality_other: embryologyData?.sperms_quality_other || "",
        //   assisted_by_other: embryologyData?.assisted_by_other || "",
        //   embryo_dev_batch_no_exp_date:
        //     embryologyData?.embryo_dev_batch_no_exp_date || "",
        //   tesa_pesa_done_by_other:
        //     embryologyData?.tesa_pesa_done_by_other || "",
        add_on_other: embryologyData?.add_on_other || "",
        embryo_dev_batch_no: embryologyData?.embryo_dev_batch_no || "--",
        embryo_dev_expiry_date: embryologyData?.embryo_dev_expiry_date
          ? moment(embryologyData?.embryo_dev_expiry_date).format("DD/MM/YYYY")
          : null,

        //   day_0_time: embryologyData?.day_0_time
        //     ? dayjs(embryologyData?.day_0_time, "HH:mm:ss").format("HH:mm:ss")
        //     : null,

        //   day_0_hrs_post_icsi: embryologyData?.day_0_hrs_post_icsi || "--",

        day_3_time: embryologyData?.day_3_time
          ? dayjs(embryologyData?.day_3_time, "HH:mm:ss").format("HH:mm:ss")
          : null,
        //   day_5_date: embryologyData?.day_5_date
        //     ? moment(embryologyData?.day_5_date).format("DD/MM/YYYY")
        //     : null,
        //   day_6_date: embryologyData?.day_6_date
        //     ? moment(embryologyData?.day_6_date).format("DD/MM/YYYY")
        //     : null,
        //   vitrification_batch_no_exp_date:
        //     embryologyData?.vitrification_batch_no_exp_date || "--",
        //   freezing_media: embryologyData?.freezing_media || null,
        //   vitrification_devices: embryologyData?.vitrification_devices || null,
        vitrification_batch_no: embryologyData?.vitrification_batch_no || "--",
        vitrification_expiry_date: embryologyData?.vitrification_expiry_date
          ? moment(embryologyData?.vitrification_expiry_date).format(
              "DD/MM/YYYY"
            )
          : null,
        pre_frozen_embryo_available:
          embryologyData?.pre_frozen_embryo_available || null,
        // no_of_goblet: embryologyData?.no_of_goblet || "--",
        //   tank_no: embryologyData?.tank_no || "",
        // no_of_straws: embryologyData?.no_of_straws || "--",
        //   cannister_no: embryologyData?.cannister_no || "",
        thawing_media: embryologyData?.thawing_media || null,
        //   embryo_dev_incubator_other:
        //     embryologyData?.embryo_dev_incubator_other || "",
        warming_expiry_date: embryologyData?.warming_expiry_date
          ? moment(embryologyData?.warming_expiry_date).format("DD/MM/YYYY")
          : null,
        distance_from_fundus: embryologyData?.distance_from_fundus || "--",
        transfer_done_by: embryologyData?.transfer_done_by || null,
        //   total_straw: embryologyData?.total_straw || 0,
        //   total_goblet: embryologyData?.total_goblet || 0,
        embryo_loading_by: embryologyData?.embryo_loading_by || null,
        //   embryo_dev_incubator: embryologyData?.embryo_dev_incubator || null,
        total_m1: embryologyData?.total_m1 || 0,
        total_m2: embryologyData?.total_m2 || 0,
        cleaved: embryologyData?.cleaved || 0,
        no_of_oocytes: embryologyData?.no_of_oocytes || 0,
        degenerated: embryologyData?.degenerated || 0,
        unfert: embryologyData?.unfert || 0,
        abnormal_pn: embryologyData?.abnormal_pn || 0,
        kept_for_blastocyst: embryologyData?.kept_for_blastocyst || 0,
        notes: embryologyData?.notes || "--",
        //   sperms_by_donor: embryologyData?.sperms_by_donor || null
        //   eggs_donor: embryologyData?.eggs_donor || null,
        //   eggs_to_donor:
        //     embryologyData?.eggs_to_donor?.map((_) => _.name) || null,
        //   eggs_by_donor:
        //     embryologyData?.eggs_by_donor?.map((_) => _.id) || null,
        //   eggs_by_ivf_id_donor:
        //     embryologyData?.eggs_by_ivf_id_donor?.map((_) => _._id) || null,
        //   eggs_to: embryologyData?.eggs_to?.map((_) => _.name) || null,
        //   eggs_by: embryologyData?.eggs_by?.map((_) => _.id) || null,
        //   eggs_by_ivf_id:
        //     embryologyData?.eggs_by_ivf_id?.map((_) => _._id) || null
      };

      // const eggsByIvfOption =
      //   embryologyData?.eggs_by_ivf_id?.length > 0
      //     ? embryologyData?.eggs_by_ivf_id
      //     : [];

      const eggsByIvfDonorOption =
        embryologyData?.eggs_by_ivf_id_donor?.length > 0
          ? embryologyData?.eggs_by_ivf_id_donor
          : [];

      const embryologyEggsByIVFOptions =
        embryologyData?.eggs_by_ivf_id?.filter(
          (item) => item?._id && item._id !== selectedPatient?._id
        ) ?? [];

      const embryologyEggsToIVFOptions =
        embryologyData?.eggs_to_ivf_id?.filter(
          (item) => item?._id && item._id !== selectedPatient?._id
        ) ?? [];

      // dispatch(setEggsByIvfOptionTemp(eggsByIvfOption));
      dispatch(setEggsByIvfOptionTemp(embryologyEggsByIVFOptions));
      dispatch(setEggsToIvfOptionTemp(embryologyEggsToIVFOptions));
      dispatch(setEggsByIvfDonorOptionTemp(eggsByIvfDonorOption));

      // const embryologyDataObjectForForm = {
      //   ivf_flow_id: embryologyData?.ivf_flow_id,
      //   _id: embryologyData?._id,
      //   eggs: embryologyData?.eggs || null,
      //   cycle_type: embryologyDetails?.cycle_type || null,

      //   cycle_no: embryologyData?.cycle_no || "--",
      //   // opu_done_by: embryologyData?.opu_done_by || null,
      //   opu_done_by: embryologyData?.opu_done_by
      //     ? embryologyData.opu_done_by
      //     : embryologyData?.opu_done_by_other
      //     ? "Other"
      //     : null,
      //   opu_done_by_other: embryologyData?.opu_done_by_other || "",
      //   anesthesia_given_by: embryologyData?.anesthesia_given_by || null,
      //   denudation_done_by: embryologyData?.denudation_done_by || null,
      //   anesthesia_given_by_other:
      //     embryologyData?.anesthesia_given_by_other || "",
      //   denudation_done_by_other:
      //     embryologyData?.denudation_done_by_other || "",
      //   tesa_pesa_done_by: embryologyData?.tesa_pesa_done_by
      //     ? embryologyData.tesa_pesa_done_by
      //     : embryologyData?.tesa_pesa_done_by_other
      //     ? "Other"
      //     : null,
      //   // donor_partner_name: embryologyData?.donor_partner_name || "--",
      //   // donor_partner_age: embryologyData?.donor_partner_age || "--",
      //   sperms_by: embryologyData?.sperms_by || null,
      //   sperms: embryologyData?.sperms || null,
      //   sperms_quality: embryologyData?.sperms_quality || null,
      //   sperms_prep_method: embryologyData?.sperms_prep_method || null,
      //   sperms_quality_donor: embryologyData?.sperms_quality_donor || null,
      //   sperms_quality_other_donor:
      //     embryologyData?.sperms_quality_other_donor || "",
      //   sperms_prep_method_donor:
      //     embryologyData?.sperms_prep_method_donor || null,
      //   sperms_prep_method_other_donor:
      //     embryologyData?.sperms_prep_method_other_donor || "",
      //   sperms_donor: embryologyData?.sperms_donor || null,
      //   sperms_by_donor: embryologyData?.sperms_by_donor || null,
      //   frozen_vial_id_donor: embryologyData?.frozen_vial_id_donor || "",
      //   add_on: embryologyData?.add_on || null,
      //   frozen_vial_id: embryologyData?.frozen_vial_id || "",
      //   icsi_ivf_done_by: embryologyData?.icsi_ivf_done_by
      //     ? embryologyData.icsi_ivf_done_by
      //     : embryologyData?.icsi_ivf_done_by_other
      //     ? "Other"
      //     : null,
      //   assisted_by: embryologyData?.assisted_by
      //     ? embryologyData.assisted_by
      //     : embryologyData?.assisted_by_other
      //     ? "Other"
      //     : null,
      //   opu_date: embryologyData?.opu_date
      //     ? dayjs(
      //         moment(embryologyData?.opu_date).format("DD/MM/YYYY"),
      //         "DD/MM/YYYY"
      //       )
      //     : null,
      //   opu_time: embryologyData?.opu_time
      //     ? dayjs(embryologyData?.opu_time, "HH:mm:ss")
      //     : null,
      //   actual_opu_time: embryologyData?.actual_opu_time
      //     ? dayjs(embryologyData?.actual_opu_time, "HH:mm:ss")
      //     : null,
      //   amh: embryologyData?.amh || "--",
      //   progesterone: embryologyData?.progesterone || "--",
      //   // lmp: (embryologyData?.lmp !== '--' && embryologyData?.lmp)
      //   //   ? dayjs(
      //   //     moment(embryologyData?.lmp).format(
      //   //       "DD/MM/YYYY"
      //   //     ),
      //   //     "DD/MM/YYYY"
      //   //   )
      //   //   : "",
      //   lh: embryologyData?.lh || "--",
      //   e2: embryologyData?.e2 || "--",
      //   atretic: embryologyData?.atretic || "",
      //   embryo_dev_pgt: embryologyData?.embryo_dev_pgt || null,
      //   denudation_time: embryologyData?.denudation_time
      //     ? dayjs(embryologyData?.denudation_time, "HH:mm:ss")
      //     : null,
      //   icsi_ivf_time: embryologyData?.icsi_ivf_time
      //     ? dayjs(embryologyData?.icsi_ivf_time, "HH:mm:ss")
      //     : null,
      //   embryo_dev_culture_media:
      //     embryologyData?.embryo_dev_culture_media || null,
      //   freezing_media_other: embryologyData?.freezing_media_other || null,
      //   vitrification_devices_other:
      //     embryologyData?.vitrification_devices_other || null,
      //   icsi_ivf_done_by_other: embryologyData?.icsi_ivf_done_by_other || "",
      //   sperms_prep_method_other:
      //     embryologyData?.sperms_prep_method_other || "",
      //   sperms_quality_other: embryologyData?.sperms_quality_other || "",
      //   assisted_by_other: embryologyData?.assisted_by_other || "",
      //   embryo_dev_culture_media_other:
      //     embryologyData?.embryo_dev_culture_media_other || "",
      //   embryo_dev_batch_no_exp_date:
      //     embryologyData?.embryo_dev_batch_no_exp_date || "",
      //   tesa_pesa_done_by_other:
      //     embryologyData?.tesa_pesa_done_by_other || "",
      //   add_on_other: embryologyData?.add_on_other || "",
      //   embryo_dev_batch_no: embryologyData?.embryo_dev_batch_no || "--",
      //   embryo_dev_expiry_date: embryologyData?.embryo_dev_expiry_date
      //     ? dayjs(
      //         moment(embryologyData?.embryo_dev_expiry_date).format(
      //           "DD/MM/YYYY"
      //         ),
      //         "DD/MM/YYYY"
      //       )
      //     : null,
      //   day_0_time: embryologyData?.day_0_time
      //     ? dayjs(embryologyData?.day_0_time, "HH:mm:ss")
      //     : null,

      //   day_0_hrs_post_icsi: embryologyData?.day_0_hrs_post_icsi || "--",

      //   day_3_time: embryologyData?.day_3_time
      //     ? dayjs(embryologyData?.day_3_time, "HH:mm:ss")
      //     : null,
      //   day_5_date: embryologyData?.day_5_date
      //     ? dayjs(
      //         moment(embryologyData?.day_5_date).format("DD/MM/YYYY"),
      //         "DD/MM/YYYY"
      //       )
      //     : null,
      //   day_6_date: embryologyData?.day_6_date
      //     ? dayjs(
      //         moment(embryologyData?.day_6_date).format("DD/MM/YYYY"),
      //         "DD/MM/YYYY"
      //       )
      //     : null,
      //   vitrification_batch_no_exp_date:
      //     embryologyData?.vitrification_batch_no_exp_date || "--",
      //   freezing_media: embryologyData?.freezing_media || null,
      //   vitrification_devices: embryologyData?.vitrification_devices || null,
      //   vitrification_batch_no:
      //     embryologyData?.vitrification_batch_no || "--",
      //   vitrification_expiry_date: embryologyData?.vitrification_expiry_date
      //     ? dayjs(
      //         moment(embryologyData?.vitrification_expiry_date).format(
      //           "DD/MM/YYYY"
      //         ),
      //         "DD/MM/YYYY"
      //       )
      //     : null,
      //   pre_frozen_embryo_available:
      //     embryologyData?.pre_frozen_embryo_available || null,
      //   tank_no: embryologyData?.tank_no || "",
      //   // no_of_straws: embryologyData?.no_of_straws || "--",
      //   cannister_no: embryologyData?.cannister_no || "",
      //   thawing_media: embryologyData?.thawing_media || null,
      //   embryo_dev_incubator_other:
      //     embryologyData?.embryo_dev_incubator_other || "",
      //   warming_expiry_date: embryologyData?.warming_expiry_date
      //     ? dayjs(
      //         moment(embryologyData?.warming_expiry_date).format(
      //           "DD/MM/YYYY"
      //         ),
      //         "DD/MM/YYYY"
      //       )
      //     : null,
      //   distance_from_fundus: embryologyData?.distance_from_fundus || "--",
      //   transfer_done_by: embryologyData?.transfer_done_by || null,
      //   total_straw: embryologyData?.total_straw || 0,
      //   total_goblet: embryologyData?.total_goblet || 0,
      //   embryo_loading_by: embryologyData?.embryo_loading_by || null,
      //   embryo_dev_incubator: embryologyData?.embryo_dev_incubator || null,
      //   notes: embryologyData?.notes || "--",
      //   cleaved: embryologyData?.cleaved || 0,
      //   degenerated: embryologyData?.degenerated || 0,
      //   unfert: embryologyData?.unfert || 0,
      //   abnormal_pn: embryologyData?.abnormal_pn || 0,
      //   kept_for_blastocyst: embryologyData?.kept_for_blastocyst || 0,
      //   no_of_oocytes: embryologyData?.no_of_oocytes || 0,
      //   eggs_donor: embryologyData?.eggs_donor || null,
      //   eggs_to_donor:
      //     embryologyData?.eggs_to_donor?.map((_) => _.name) || null,
      //   eggs_by_donor:
      //     embryologyData?.eggs_by_donor?.map((_) => _.id) || null,
      //   eggs_by_ivf_id_donor:
      //     embryologyData?.eggs_by_ivf_id_donor?.map((_) => _._id) || null,
      //   eggs_to: embryologyData?.eggs_to?.map((_) => _.name) || null,
      //   eggs_by: embryologyData?.eggs_by?.map((_) => _.id) || null,
      //   eggs_by_ivf_id:
      //     embryologyData?.eggs_by_ivf_id?.map((_) => _._id) || null,
      //   ...newPackageData
      // };

      dispatch(setBackupIvfList(embryologyEggsByIVFOptions));
      // dispatch(setBackupIvfList(eggsByIvfOption));
      dispatch(setBackupIvfDonerList(eggsByIvfDonorOption));

      // setDonateByIVFData(embryologyData?.donateByIVFData);

      // setDonateByIVFDataDonor(embryologyData?.donateByIVFDataDonor);
      dispatch(setEmbryologyDetails(embryologyDataObject));
      // setEmbryologyDetails(embryologyDataObject);
      // form.setFieldsValue(embryologyDataObjectForForm);

      if (isSetData) {
        dispatch(setEmbryologyDetails(embryologyDataObject));
        // setEmbryologyDetails(embryologyDataObject);
        //   form.setFieldsValue(embryologyDataObjectForForm);
      }

      return { embryologyDataObject, procedureDetailsObj };
      // }
    },
    [dispatch, embryologyDetails, selectedPatient]
  );

  useEffect(() => {
    if (ivfIdListData?.length > 0) {
      const ivfListId = ivfIdListData?.map((item) => ({
        value: item._id,
        label: item.ivf_id,
        protocol: item.protocol,
      }));

      dispatch(setIvfIdOption(ivfListId));

      dispatch(
        setEmbryologyDetails({
          ...embryologyDetails,
          ivf_flow_id: ivfListId[0]?.value,
          protocol: ivfListId[0]?.protocol,
        })
      );
    }
  }, [dispatch, ivfIdListData]);

  // const onFinishFailed = (errorInfo) => {
  //   const firstErrorField = document.querySelector(".ant-form-item-has-error");
  //   if (firstErrorField) {
  //     firstErrorField.scrollIntoView({ behavior: "smooth" });
  //   }
  // };

  const clearEmbryologyData = useCallback(() => {
    // setPatientDetails({
    //   patient_id: "",
    //   patient_dob: "",
    //   patient_full_name: "",
    //   partner_full_name: "",
    // });
    // dispatch(patientDetailsEDS({}))
    // dispatch(setPartnerDetailsEDS({}))
    setPrintOption([]);
    setEmbryoTableList([]);
    dispatch(setIvfIdOption([]));
    dispatch(setEmbryologyDetails(clearEmbryologyDetailsData));
    // setEmbryologyDetails(clearEmbryologyDetailsData);
    // form.resetFields();
  }, [dispatch]);

  const handleClear = () => {
    clearEmbryologyData();
    dispatch(setSelectedPatient({}));
    dispatch(setIvfIdList([]));
    dispatch(clearData());
    dispatch(setEggsByBackupList([]));
    dispatch(setIvfFlowsheetList({}));
  };

  useEffect(() => {
    const slider = document.querySelector(
      ".custom_table_Wrap .table-responsive"
    );
    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    slider.addEventListener("mouseleave", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mouseup", () => {
      isDown = false;
      slider.classList.remove("active");
    });
    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1;
      slider.scrollLeft = scrollLeft - walk;
    });

    return () => {
      dispatch(setIvfIdListData([]));
      dispatch(setPatientDetailsEDS(initialPatientDetailsEDS));
      dispatch(setPartnerDetailsEDS({}));
      dispatch(setEmbryologyDetails(embryologyInitialData));
    };
  }, [dispatch]);

  const getNewSelectedPatientData = useCallback(async () => {
    if (
      embryologyDataUpdate &&
      Object.keys(selectedPatient)?.length > 0 &&
      Object.keys(embryologyData)?.length === 0
    ) {
      const { payload } = await dispatch(
        getGlobalSearch({
          patient_reg_id: selectedPatient._id,
          patient_name: selectedPatient.patient_full_name,
          location_id: selectedLocation,
        })
      );
      if (payload.length > 0) dispatch(setSelectedPatient(payload[0]));
    }
  }, [
    dispatch,
    selectedLocation,
    selectedPatient,
    embryologyDataUpdate,
    embryologyData,
  ]);

  useEffect(() => {
    getNewSelectedPatientData();
  }, [embryologyDataUpdate]);

  const printVitrificationReportData = useCallback(
    async (val) => {
      dispatch(
        printVitrificationReport({
          location_id: selectedLocation,
          moduleId: selectedModule?._id,
          patientRegId: selectedPatient?._id,
          ivfFlowId: embryologyData?.ivf_flow_id,
          payload: {
            stage: val,
          },
        })
      );
    },
    [selectedModule, dispatch, embryologyData, selectedPatient]
  );

  const printBlankPdfEmbryoTransferDevelopment = useCallback(async () => {
    dispatch(
      printBlankPdfEmbryoTransfer({
        location_id: selectedLocation,
        moduleId: selectedModule?._id,
        ivfFlowId: embryologyData?.ivf_flow_id,
        patientRegId: selectedPatient?._id,
      })
    );
  }, [embryologyData, dispatch, selectedModule, selectedPatient]);

  const handleEggsByChange = (newValue, field) => {
    if (field === "eggs_by") {
      // take the patient-Payal data
      const patientDetails = patientList?.filter((patient) =>
        newValue.includes(patient?._id)
      );

      // alredy selected patients ids
      const existingPatientIds =
        eggBySelectedList?.map((patient) => patient._id) || [];

      // check, if new patient is selected
      const newPatients = patientDetails?.filter(
        (patient) => !existingPatientIds.includes(patient._id)
      );

      let array = [];

      // If new patient is selected
      if (newPatients.length !== 0) {
        const newPatientObj = JSON.parse(JSON.stringify(newPatients[0]));

        // get all previous stored ivf ids data
        const eggsByBackupData = eggsByBackupList.flatMap(
          (item) => item?.ivf_flow_data || []
        );

        setEggsByBackupList([...eggsByBackupList, newPatientObj]);
        // setEggsByBackupList((prev) => [...prev, newPatientObj]);

        newPatientObj["ivf_flow_data"].map((item) => {
          array.push(item);
        });

        if (backupIvfList?.length > 0) {
          backupIvfList.map((item) => {
            array.push({ ...item, disabled: true });
          });
        }
        dispatch(setEggsByIvfOptionTemp([...array, ...eggsByBackupData]));
      }

      // If patient is removed
      if (newPatients.length === 0) {
        const removedItems = embryologyDetails?.eggs_by?.filter(
          (item) => !newValue.includes(item)
        );

        const removedPatientDetails = eggsByBackupList?.filter((patient) =>
          removedItems?.includes(patient?._id)
        );

        // const isRemoved = removedItems?.includes(donateByIVFData?._id);

        if (removedPatientDetails) {
          const removedIVFIds = removedPatientDetails.flatMap((patient) =>
            patient.ivf_flow_data.map((ivf) => ivf._id)
          );

          const exceptedEggsByIvfOption = eggsByIvfOptionTemp?.filter(
            (item) => {
              return !removedIVFIds?.includes(item._id);
            }
          );

          // donateByIVFData.ivf_flow_data = donateByIVFData.ivf_flow_data.filter(
          //   (ivf) => !removedIVFIds?.includes(ivf.ivf_id)
          // );

          dispatch(setEggsByIvfOptionTemp(exceptedEggsByIvfOption));
        }
      }

      dispatch(
        setEmbryologyDetails({
          ...embryologyDetails,
        })
      );

      const mergedPatientDetails = [...eggBySelectedList, ...patientDetails];

      setEggBySelectedList(mergedPatientDetails);

      let newA = [...embryology_count];

      // Managed embryo table if remove patient from the list
      // Create new objects for selected values that are not already in dataArray
      newValue.map((id) => {
        if (embryology_count.length === 0) {
          newA.push({ ...embryology_countTable, embryo: [] });
        } else if (embryology_count.length > 0) {
          //Check if the id exists in the array
          const find = embryology_count.find(
            (item) => item.patient_detail.patientId === id
          );
          if (!find) newA.push({ ...embryology_countTable, embryo: [] });
        }
      });

      if (newValue.length < eggBySelectedList.length) {
        newA = newA.filter(
          (item1) =>
            item1.patient_detail.isDonor ||
            newValue.some((a) => a === item1.patient_detail.patientId)
        );
        // setEmbryology_count(newA);
        // formikRef.current?.setFieldValue("oocytes", newA);
        setFieldValue("oocytes", newA);
      } else if (newValue.length === 0) {
        const idsToRemove = eggBySelectedList?.map((donor) => donor._id);
        const filteredNewA = newA?.filter(
          (item) => !idsToRemove?.includes(item.patient_detail.patientId)
        );
        // setEmbryology_count(filteredNewA);
        // formikRef.current?.setFieldValue("oocytes", filteredNewA);
        setFieldValue("oocytes", filteredNewA);
      }
      // remove objects that are not in the selected values

      const removedItems = embryologyDetails?.eggs_by?.find(
        (item) => !newValue.includes(item)
      );

      const isCurrentPatientRemoved = selectedPatient?._id === removedItems;

      let ivfIdList = JSON.parse(JSON.stringify(backupIvfList));

      if (removedItems && !isCurrentPatientRemoved) {
        ivfIdList = ivfIdList.filter(
          (item) => item.patient_reg_id !== removedItems
        );

        if (patientList.length) {
          const copyDonateByIVFDataDonor = JSON.parse(
            JSON.stringify(eggsByIvfOptionTemp)
          );
          const newData = copyDonateByIVFDataDonor.filter(
            (ivf) => ivf.patient_reg_id !== removedItems
          );

          dispatch(setEggsByIvfOptionTemp(newData));
        }
        dispatch(setBackupIvfList(ivfIdList));
        const removedEggBySelectedList = eggBySelectedList.filter(
          (item) => item._id !== removedItems
        );

        const eggsByPreviousBackupList = eggsByBackupList?.filter(
          (patient) => !removedItems?.includes(patient?._id)
        );

        setEggBySelectedList(removedEggBySelectedList);
        dispatch(setEggsByBackupList(eggsByPreviousBackupList));
      }

      dispatch(
        setPatientDetailsEDS({
          ...patientDetailsEDS,
          eggs_by: newValue,
          // eggs_by_ivf_id: ivfIdList.map((item) => item._id)
        })
      );
    }

    // else {
    //   setEmbryologyDetails({
    //     ...embryologyDetails,
    //     sperms_by: newValue
    //   });
    //   form.setFieldsValue({
    //     sperms_by: newValue
    //   });
    // }
  };

  const onChangeEggsTo = (newValue, field) => {
    let updatedEggsToIvfId = [...(patientDetailsEDS?.eggs_to_ivf_id ?? [])];

    const currentWithBackupOptionsList = _.uniqBy(
      [...eggsToPatientList, ...eggsToBackupList],
      "_id"
    );

    const findOptionFromList = currentWithBackupOptionsList?.filter((item) =>
      newValue.includes(item?._id)
    );

    const added = _.find(
      findOptionFromList,
      (item) =>
        _.includes(newValue, item._id) &&
        !_.some(eggsToBackupList, { _id: item._id })
    );

    const removed = _.find(
      eggsToBackupList,
      (item) => !_.includes(newValue, item._id)
    );

    if (!!added) {
      if (!!added?.ivf_flow_data?.length) {
        // let eggsToIvfOptionsList = _.uniqBy([...eggsToIvfOptionTemp], "_id");

        let eggsToIvfFlowData = JSON.parse(JSON.stringify(added.ivf_flow_data));

        // This logic applies to removing options from the newly selected patient that are previously saved options in the Eggs-to-IVF-Id.
        if (
          embryologyData?.eggs_to_ivf_id?.length > 0 &&
          selectedPatient?._id
        ) {
          const previousSevedEmbryologyEggsToIVFOptions =
            embryologyData.eggs_to_ivf_id.filter(
              (item) => item._id && item._id !== selectedPatient._id
            ) ?? [];

          if (previousSevedEmbryologyEggsToIVFOptions?.length > 0) {
            eggsToIvfFlowData = eggsToIvfFlowData.filter(
              (item) =>
                !previousSevedEmbryologyEggsToIVFOptions.some(
                  (prev) => prev._id === item._id
                )
            );
          }
        }

        const generatedOptions =
          eggsToIvfOptionTemp?.length > 0
            ? [...eggsToIvfFlowData, ...eggsToIvfOptionTemp]
            : added.ivf_flow_data;

        dispatch(setEggsToIvfOptionTemp(generatedOptions));
      }
    } else if (!!removed) {
      if (!!removed?.ivf_flow_data?.length) {
        updatedEggsToIvfId = [];

        const exceptRemovedEggsToIvfOptions = eggsToIvfOptionTemp?.filter(
          (item) => {
            const isValid =
              item.patient_reg_id !== removed._id && !item?.disable;

            if (isValid) {
              updatedEggsToIvfId.push(item._id);
            }
            return isValid;
          }
        );

        dispatch(setEggsToIvfOptionTemp(exceptRemovedEggsToIvfOptions));
      }
    }

    dispatch(setEggsToPatientList(findOptionFromList));
    dispatch(setEggsToBackupList(findOptionFromList));

    dispatch(
      setPatientDetailsEDS({
        ...patientDetailsEDS,
        [field]: newValue || null,
        eggs_to_ivf_id: updatedEggsToIvfId,
      })
    );
  };

  const handleDonorChange = (newValue, field) => {
    if (field === "eggs_by_donor") {
      const patientDetails = patientList?.filter((patient) =>
        newValue.includes(patient?._id)
      );
      const existingPatientIds =
        eggBySelectedListDonor?.map((patient) => patient._id) || [];

      const newPatients = patientDetails?.filter(
        (patient) => !existingPatientIds.includes(patient._id)
      );
      let array = [];

      if (newPatients.length !== 0) {
        const newPatientObj = JSON.parse(JSON.stringify(newPatients[0]));

        const updatedBackupList = [...eggsByDonorBackupList, newPatientObj];
        // dispatch(setEggsByDonorBackupList((prev) => [...prev, newPatientObj]));
        dispatch(setEggsByDonorBackupList(updatedBackupList));

        newPatientObj["ivf_flow_data"].map((item) => {
          array.push(item);
        });
        if (backupIvfDonerList.length > 0) {
          backupIvfDonerList.map((item) => {
            array.push({ ...item, disabled: true });
          });
        }
        dispatch(setEggsByIvfDonorOptionTemp(array));
      }

      if (newPatients.length === 0) {
        // const removedItems = embryologyDetails?.eggs_by_donor?.filter(
        //   (item) => !newValue.includes(item)
        // );

        const removedItems = patientDetailsEDS?.eggs_by_donor?.filter(
          (item) => !newValue.includes(item)
        );

        // dispatch(
        //   setEggsByDonorBackupList((prev) =>
        //     prev?.filter((patient) => !removedItems?.includes(patient?._id))
        //   )
        // );

        const backupFilteredList = eggsByDonorBackupList?.filter(
          (patient) => !removedItems?.includes(patient?._id)
        );

        const updatedBackupList = [
          ...eggsByDonorBackupList,
          ...backupFilteredList,
        ];
        dispatch(setEggsByDonorBackupList(updatedBackupList));

        const removedPatientDetails = patientList?.filter((patient) =>
          removedItems.includes(patient?._id)
        );

        const isRemoved = removedItems?.includes(donateByIVFDataDonor?._id);

        if (isRemoved) {
          const removedIVFIds = removedPatientDetails.flatMap((patient) =>
            patient.ivf_flow_data.map((ivf) => ivf.ivf_id)
          );

          donateByIVFDataDonor.ivf_flow_data =
            donateByIVFDataDonor.ivf_flow_data.filter(
              (ivf) => !removedIVFIds.includes(ivf.ivf_id)
            );
          dispatch(
            setEggsByIvfDonorOptionTemp(donateByIVFDataDonor.ivf_flow_data)
          );

          // setDonateByIVFDataDonor(donateByIVFDataDonor);
        }
      }

      dispatch(
        setPatientDetailsEDS({
          ...patientDetailsEDS,
        })
      );

      // dispatch(
      //   setEmbryologyDetails({
      //     ...embryologyDetails
      //   })
      // );

      const mergedPatientDetails = [
        ...eggBySelectedListDonor,
        ...patientDetails,
      ];

      setEggBySelectedListDonor(mergedPatientDetails);

      let newA = [...embryology_count];

      // Create new objects for selected values that are not already in dataArray
      newValue.map((id) => {
        if (embryology_count.length === 0) {
          newA.push({ ...embryology_countTable, embryo: [] });
        } else if (embryology_count.length > 0) {
          //Check if the id exists in the array
          const find = embryology_count.find(
            (item) => item.patient_detail.patientId === id
          );
          if (!find) newA.push({ ...embryology_countTable, embryo: [] });
        }
      });
      if (newValue.length < eggBySelectedListDonor.length) {
        newA = newA.filter(
          (item1) =>
            !item1.patient_detail.isDonor ||
            newValue.some((id) => id === item1.patient_detail.patientId)
        );
        // dispatch(setEmbryology_count(newA));
        // formikRef.current?.setFieldValue("oocytes", newA);
        setFieldValue("oocytes", newA);
      } else if (newValue.length === 0) {
        const idsToRemove = eggBySelectedListDonor?.map((donor) => donor._id);
        const filteredNewA = newA?.filter(
          (item) => !idsToRemove?.includes(item.patient_detail.patientId)
        );
        // dispatch(setEmbryology_count(filteredNewA));
        // formikRef.current?.setFieldValue("oocytes", filteredNewA);
        setFieldValue("oocytes", filteredNewA);

        // setEmbryologyDetails({
        //   ...embryologyDetails,
        //   eggs_by_donor: [],
        //   eggs_by_ivf_id_donor: [],
        // }));

        // setBackupIvfDonerList([]);
      }
      // remove objects that are not in the selected values
      // let filteredArray = newA.filter((item1) => {
      //   return newValue.some((id) => id === item1.patient_id);
      // });

      // remove objects that are not in the selected values
      // const removedItems = embryologyDetails?.eggs_by_donor?.find(
      //   (item) => !newValue.includes(item)
      // );
      const removedItems = patientDetailsEDS?.eggs_by_donor?.find(
        (item) => !newValue.includes(item)
      );

      let ivfIdList = JSON.parse(JSON.stringify(backupIvfDonerList));

      if (removedItems) {
        ivfIdList = ivfIdList.filter(
          (item) => item.patient_reg_id !== removedItems
        );
        if (patientList.length) {
          const copyDonateByIVFDataDonor = JSON.parse(
            JSON.stringify(eggsByIvfDonorOptionTemp)
          );
          const newData = copyDonateByIVFDataDonor.filter(
            (ivf) => ivf.patient_reg_id !== removedItems
          );

          dispatch(setEggsByIvfDonorOptionTemp(newData));
        }
        dispatch(setBackupIvfDonerList(ivfIdList));
        const removedEggBySelectedList = eggBySelectedListDonor.filter(
          (item) => item._id !== removedItems
        );

        setEggBySelectedListDonor(removedEggBySelectedList);
        // dispatch(
        //   setEggsByDonorBackupList((prev) =>
        //     prev?.filter((patient) => !removedItems?.includes(patient?._id))
        //   )
        // );

        const backupFilteredList = eggsByDonorBackupList?.filter(
          (patient) => !removedItems?.includes(patient?._id)
        );

        const updatedBackupList = [
          ...eggsByDonorBackupList,
          ...backupFilteredList,
        ];
        dispatch(setEggsByDonorBackupList(updatedBackupList));
      }

      // setEmbryologyDetails({
      //   ...embryologyDetails,
      //   // eggs_by_ivf_id_donor: null,
      //   eggs_by_donor: newValue,
      //   eggs_by_ivf_id_donor: ivfIdList.map((item) => item._id)
      // });

      // dispatch(
      //   setEmbryologyDetails({
      //     ...embryologyDetails,
      //     // eggs_by_ivf_id_donor: null,
      //     eggs_by_donor: newValue,
      //     eggs_by_ivf_id_donor: ivfIdList.map((item) => item._id)
      //   })
      // );

      dispatch(
        setPatientDetailsEDS({
          ...patientDetailsEDS,
          eggs_by_donor: newValue,
          eggs_by_ivf_id_donor: ivfIdList.map((item) => item._id),
        })
      );
    }
  };

  const handleClearSearch = useCallback(() => {
    dispatch(setPatientListData([]));
    // setEggByDetail({});
    // setPDetail({});
    setDonateByIVFData([]);
    dispatch(
      setPartnerDetailsEDS({
        ...partnerDetailsEDS,
        sperms_by: null,
        sperms_by_donor: null,
      })
    );

    // dispatch(
    //   setEmbryologyDetails({
    //     ...embryologyDetails,
    //     eggs_by_ivf_id: null
    //   })
    // );
  }, [dispatch, partnerDetailsEDS]);

  const handleClearSearchDonor = useCallback(() => {
    // dispatch(setPatientListData([]));
    dispatch(setPatientDetailsEDS(initialPatientDetailsEDS));
    setDonateByIVFDataDonor([]);
    // form.setFieldsValue({
    //   eggs_by_donor: null,
    //   sperms_by_donor: null,
    //   eggs_by_ivf_id_donor: null
    // });

    // form.setFieldsValue({
    //   eggs_by_ivf_id: null,
    // });
  }, [dispatch]);

  const menuProps = (
    <Menu>
      {printOption?.map((item) => {
        return (
          <Menu.Item
            onClick={() => {
              selectedModule?._id &&
                selectedPatient?._id &&
                embryologyData?.ivf_flow_id &&
                printVitrificationReportData(item?.value);
            }}
            key="1"
          >
            {item?.label}
          </Menu.Item>
        );
      })}
    </Menu>
  );

  // const handleSubmit = (e) => {
  //   e.preventDefault();

  //   if (formikRef.current) {
  //     formikRef.current.submitForm();
  //   }
  // };

  return (
    <div className="page_main_content">
      <div className="page_inner_content">
        {(IvfFlowsheetListLoding ||
          embryologyDataLoading ||
          embryologyRedirect) && (
          <Spin tip="Loading" size="large">
            <div className="content" />
          </Spin>
        )}

        <form>
          <div className="form_process_wrapper">
            <div className="form_info_wrapper filled">
              <div className="patient_detail_wrap">
                <ul>
                  <li>
                    <label>Patient ID :</label>
                    <span>
                      {selectedPatient?.patient_id
                        ? selectedPatient?.patient_id
                        : ""}
                    </span>
                  </li>
                  <li>
                    <label>Patient Name :</label>
                    <span>
                      {selectedPatient?.patient_full_name
                        ? selectedPatient?.patient_full_name
                        : ""}
                    </span>
                  </li>
                  <li>
                    <label>Age :</label>
                    <span>
                      {selectedPatient?.patient_dob
                        ? ageCalculate(selectedPatient?.patient_dob)
                        : ""}
                    </span>
                  </li>
                  <li>
                    <label>Partner Name :</label>
                    <span>
                      {selectedPatient?.partner_full_name
                        ? selectedPatient?.partner_full_name
                        : ""}
                    </span>
                  </li>
                  <li>
                    <label>Age :</label>
                    <span>
                      {selectedPatient?.partner_dob
                        ? ageCalculate(selectedPatient?.partner_dob)
                        : ""}
                    </span>
                  </li>
                  <li>
                    <label>Married Since :</label>
                    <span>
                      {selectedPatient?.married_since
                        ? selectedPatient?.married_since
                        : ""}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <EmbryologyPatientDetails
              // formikRef={formikRef}
              formValues={values}
              setFieldValue={setFieldValue}
              handleEggsByChange={handleEggsByChange}
              onChangeEggsTo={onChangeEggsTo}
              // handleClearSearch={handleClearSearch}
              handleDonorChange={handleDonorChange}
              // handleClearSearchDonor={handleClearSearchDonor}
            />

            <EmbryologyProcedureDetails
              // ref={formikRef}
              values={values}
              setValues={setValues}
              errors={errors}
              touched={touched}
              setFieldError={setFieldError}
              handleBlur={handleBlur}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              selectedModule={selectedModule}
              selectedPatient={selectedPatient}
              selectedLocation={selectedLocation}
            />
          </div>
          <div className="button_group d-flex align-items-center justify-content-center mt-4">
            {Object.keys(embryologyData)?.length > 0
              ? (userType === 1 || selectedModule?.edit) && (
                  <Tooltip
                    title={
                      loggedUserData?.other
                        ? hasExceededSixMonths
                          ? "You cannot update this record after 6 months from the creation, so please contact the Admin"
                          : ""
                        : ""
                    }
                  >
                    <Button
                      disabled={
                        Object.keys(selectedPatient)?.length === 0 ||
                        (loggedUserData?.other && hasExceededSixMonths)
                      }
                      className="btn_primary me-3"
                      onClick={handleSubmit}
                      // htmlType="submit"
                    >
                      Update
                    </Button>
                  </Tooltip>
                )
              : (userType === 1 || selectedModule?.create) && (
                  <Button
                    disabled={Object.keys(selectedPatient)?.length === 0}
                    className="btn_primary me-3"
                    // htmlType="submit"
                    onClick={handleSubmit}
                  >
                    Save
                  </Button>
                )}
            <Button className="btn_gray" onClick={handleClear}>
              Cancel
            </Button>
            <Dropdown
              disabled={printOption.length === 0 ? true : false}
              overlay={menuProps}
              className="custom_select custom_dropdown"
            >
              <Button className="btn_print mx-3">
                <Space>Print</Space>
              </Button>
            </Dropdown>
            <Button
              className="btn_primary me-3"
              onClick={() => {
                if (Object.keys(selectedPatient)?.length) {
                  printBlankPdfEmbryoTransferDevelopment();
                }
              }}
              disabled={!Object.keys(selectedPatient)?.length}
            >
              Blank PDF
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
